/**
 * PFAssignmentBlock
 * ─────────────────────────────────────────────────────────────
 * Inline block shown on Position Folder panels. Surfaces:
 *
 *   • "Opened by"   — immutable, = folder.created_by
 *   • "Operations"  — = folder.assignee (current ops owner)
 *                     with a Reassign dropdown picking any
 *                     europacific_staff user. Reassignment
 *                     emails new owner + previous owner +
 *                     ops manager and writes an audit entry
 *                     to folder.assignment_history.
 *   • Last 3 reassignment audit lines (collapsed-friendly).
 *
 * On reassign, calls POST /api/position-folders/{id}/reassign
 * and invokes onUpdate() so the parent refetches.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "../config/api";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { UserCheck, UserCog, History, Loader2, X } from "lucide-react";

const prettyName = (email) => {
  if (!email) return "—";
  const local = email.split("@")[0] || "";
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(" ");
};

export default function PFAssignmentBlock({ folder, onUpdate }) {
  const token =
    (typeof window !== "undefined" && localStorage.getItem("token")) || "";
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [staff, setStaff] = useState([]);
  const [showReassign, setShowReassign] = useState(false);
  const [selected, setSelected] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  const history = Array.isArray(folder?.assignment_history)
    ? folder.assignment_history
    : [];
  const lastReassignments = history
    .filter((h) => h.role === "operations")
    .slice(-3)
    .reverse();

  const fetchStaff = async () => {
    if (staff.length > 0) return;
    setStaffLoading(true);
    try {
      const res = await axios.get(`${API_URL}/staff`, { headers });
      const rows = Array.isArray(res.data) ? res.data : res.data?.staff || [];
      const cleaned = rows
        .map((r) => ({
          email: (r.email || "").toLowerCase(),
          full_name: r.full_name || `${r.first_name || ""} ${r.last_name || ""}`.trim(),
          job_title: r.job_title || r.role || "",
        }))
        .filter((r) => r.email && r.email.includes("@"))
        .sort((a, b) => a.full_name.localeCompare(b.full_name));
      // Deduplicate by email
      const seen = new Set();
      const unique = cleaned.filter((r) =>
        seen.has(r.email) ? false : (seen.add(r.email), true)
      );
      setStaff(unique);
    } catch (err) {
      toast.error("Could not load staff list");
    } finally {
      setStaffLoading(false);
    }
  };

  useEffect(() => {
    if (showReassign) fetchStaff();
  }, [showReassign]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleReassign = async () => {
    if (!selected) {
      toast.error("Pick a staff member first");
      return;
    }
    if (selected.toLowerCase() === (folder.assignee || "").toLowerCase()) {
      toast.info("Already assigned to that person");
      setShowReassign(false);
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API_URL}/position-folders/${folder.id}/reassign`,
        { new_assignee_email: selected, reason: reason || undefined },
        { headers }
      );
      toast.success(
        `Reassigned to ${prettyName(selected)} — notified ${
          res.data.notified?.length || 0
        } people`
      );
      setShowReassign(false);
      setReason("");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reassign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="p-4 bg-slate-50 rounded-lg border border-slate-200"
      data-testid="pf-assignment-block"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Opened by — immutable */}
        <div>
          <Label className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <UserCheck className="h-3 w-3" />
            Opened by
          </Label>
          <div className="font-medium text-slate-800">
            {prettyName(folder.created_by)}
          </div>
          {folder.created_by && (
            <div className="text-[11px] text-slate-400 font-mono">
              {folder.created_by}
            </div>
          )}
        </div>

        {/* Operations — editable */}
        <div>
          <Label className="text-[11px] text-slate-500 flex items-center gap-1.5">
            <UserCog className="h-3 w-3 text-blue-600" />
            Operations
          </Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 truncate">
                {prettyName(folder.assignee)}
              </div>
              {folder.assignee && (
                <div className="text-[11px] text-slate-400 font-mono truncate">
                  {folder.assignee}
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowReassign((v) => !v)}
              className="h-7 px-2 text-xs"
              data-testid="pf-reassign-toggle-btn"
            >
              {showReassign ? "Cancel" : "Reassign"}
            </Button>
          </div>
        </div>
      </div>

      {/* Inline reassign form */}
      {showReassign && (
        <div
          className="mt-3 p-3 bg-white border border-blue-200 rounded-md"
          data-testid="pf-reassign-form"
        >
          <Label className="text-xs font-medium">New Operations owner</Label>
          {staffLoading ? (
            <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading staff…
            </div>
          ) : (
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full mt-1 px-3 py-2 text-sm rounded border border-slate-300 bg-white"
              data-testid="pf-reassign-select"
            >
              <option value="">— pick a staff member —</option>
              {staff.map((s) => (
                <option key={s.email} value={s.email}>
                  {s.full_name || s.email} · {s.email}
                  {s.job_title ? ` (${s.job_title})` : ""}
                </option>
              ))}
            </select>
          )}
          <Label className="text-xs font-medium mt-2 block">
            Reason (optional)
          </Label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. handover, OOO, customer request…"
            className="w-full mt-1 px-3 py-2 text-sm rounded border border-slate-300"
            data-testid="pf-reassign-reason"
          />
          <p className="text-[11px] text-slate-500 mt-1">
            New owner, previous owner and the ops manager will be emailed.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setShowReassign(false);
                setReason("");
              }}
              disabled={saving}
              data-testid="pf-reassign-cancel"
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleReassign}
              disabled={saving || !selected}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="pf-reassign-confirm"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  Reassigning…
                </>
              ) : (
                "Reassign"
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Recent reassignment timeline */}
      {lastReassignments.length > 0 && (
        <details className="mt-3 group" data-testid="pf-assignment-history">
          <summary className="cursor-pointer text-[11px] text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <History className="h-3 w-3" />
            Reassignment history ({history.filter(h => h.role === "operations").length})
          </summary>
          <ul className="mt-2 space-y-1.5 text-[11px] text-slate-600 pl-4 border-l-2 border-slate-200">
            {lastReassignments.map((h) => (
              <li key={h.id || h.at}>
                <span className="font-mono text-slate-400">
                  {(h.at || "").slice(0, 16).replace("T", " ")}
                </span>
                {" — "}
                <span className="font-medium">{prettyName(h.from)}</span>
                <span className="mx-1">→</span>
                <span className="font-medium text-blue-700">
                  {prettyName(h.to)}
                </span>
                <span className="text-slate-400"> by {prettyName(h.by)}</span>
                {h.reason && (
                  <span className="block italic text-slate-500 ml-1">
                    “{h.reason}”
                  </span>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
